import { forwardGeocoding, reverseGeocoding } from '@/api/mapbox'
import { getFavoriteStation } from '@/api/myGeo'
import { getStationBookingInfo, getStationCoordinates, getText } from '@/api/myTermine'
import { stationMappings } from '@/api/static'

function defaultStationState() {
  return {
    showMapboxCookieDialog: false,
    initMap: false,
    mapBounds: null,
    mainPostcode: null,
    userLocationCoordinates: null, // lng, lat
    userHomeCoordinates: null,
    savedStationCoordinates: null,
    stationCoordinates: null,
    activeStationId: null,
    activeStation: null,
    hoveredStation: { id: null, highlight: false },
    favoriteStation: null,
    isLoadingStationBookingInfo: false,
    stationBookingInfo: [],
    telephoneStationIds: [],
    telephoneStationText: '',
    keineTermineStationIds: [],
    unbookableStationIds: [],
		allStations: [],
		stationsByState: [],
  }
}

export default {
  state: () => ({
    ...defaultStationState(),
    mapboxAccessToken:
      'pk.eyJ1Ijoib2VhbXRjZ2VvMSIsImEiOiJja3lteXduOWMwMmh1Mm9veWptMmpwMDZpIn0.d6pyarrrA-f57n15WYsKNw',
    mapboxJsAccessToken:
      'pk.eyJ1Ijoib2VhbXRjZ2VvMSIsImEiOiJja3lteXduOWMwMmh1Mm9veWptMmpwMDZpIn0.d6pyarrrA-f57n15WYsKNw',
  }),
  actions: {
    async getUserHomeCoordinates({ commit, rootState, state }) {
      const address = rootState.user.userAccountInfo.address
      const addressText = address.street + ' ' + address.streetNumber + ' ' + address.zip

      try {
        const response = await forwardGeocoding(addressText, state.mapboxAccessToken)
        commit('setUserHomeCoordinates', response.data.features[0].center)
      } catch (error) {
        commit('setUserHomeCoordinates', [])
        console.error(error)
      }
    },
    async getPostcode({ commit, state }, coordinates) {
      try {
        const response = await reverseGeocoding(coordinates, state.mapboxAccessToken)
        const postcode = response.data.features.find((obj) => obj.id.includes('postcode')).text
        commit('setMainPostcode', postcode)
      } catch (error) {
        console.error(error)
      }
    },
    async getSavedStationCoordinates({ commit, state }, addressText) {
      try {
        const response = await forwardGeocoding(addressText, state.mapboxAccessToken)
        commit('setSavedStationCoordinates', response.data.features[0].center)
      } catch (error) {
        commit('setSavedStationCoordinates', [])
        console.error(error)
      }
    },
    async getStationCoordinates({ commit, state }) {
      const params = {
        zipcode: state.mainPostcode,
        latitudeMin: state.mapBounds._sw?.lat || state.mapBounds._southWest.lat,
        latitudeMax: state.mapBounds._ne?.lat || state.mapBounds._northEast.lat,
        longitudeMin: state.mapBounds._sw?.lng || state.mapBounds._southWest.lng,
        longitudeMax: state.mapBounds._ne?.lng || state.mapBounds._northEast.lng,
      }

      try {
        const response = await getStationCoordinates(params)
        commit('setStationCoordinates', response.data.items)
      } catch (error) {
        commit('setStationCoordinates', [])
        console.error(error)
      }
    },
		async getAllStations({ commit, state }) {
			const params = {
				zipcode: state.mainPostcode
			};
			try {
				const response = await getStationCoordinates(params);
				response.data.items.sort((a, b) => a.bundeslandId - b.bundeslandId);
				commit('setAllStations', response.data.items);
			} catch (error) {
				commit('setAllStations', []);
				console.error(error);
			}
		},
    async getFavoriteStation({ commit }) {
      try {
        const response = await getFavoriteStation()
        commit('setFavoriteStation', response.data)
      } catch (error) {
        console.error(error)
      }
    },
    async getStationBookingInfo({ commit }, dienstleistungsangebotId) {
      try {
        const response = await getStationBookingInfo(dienstleistungsangebotId)
        commit('addStationBookingInfo', response.data)
        commit('setIsLoadingStationBookingInfo', false)
      } catch (error) {
        console.error(error)
      }
    },
    async getTelephoneStationText({ commit }) {
      try {
        const response = await getText(473)
        commit('setTelephoneStationText', response.data.text)
      } catch (error) {
        console.error(error)
      }
    },
    async getStationMappings({ commit }, stationSlug) {
      try {
        const response = await stationMappings(stationSlug)
        commit('setActiveStation', {
          dienststelleId: +response.data.dienststelleId,
          landesclubId: +response.data.landesclubId,
          bezeichnung: response.data.bezeichnung,
          adresse: response.data.adresse,
          plz: response.data.plz,
          ort: response.data.ort,
        })
        commit('setActiveStationId', +response.data.dienststelleId)
      } catch (error) {
        commit('setActiveStationId', false)
        console.error(error)
      }
    },
  },
  mutations: {
    resetStationState(state) {
      Object.assign(state, {
        ...state,
        ...defaultStationState(),
      })
    },
    setShowMapboxCookieDialog(state, payload) {
      state.showMapboxCookieDialog = payload
    },
    setInitMap(state, payload) {
      state.initMap = payload
    },
    setMapBounds(state, payload) {
      state.mapBounds = payload
    },
    setMainPostcode(state, payload) {
      // User location
      // OR User home address
      // OR Search result
      state.mainPostcode = payload
    },
    setUserLocationCoordinates(state, payload) {
      state.userLocationCoordinates = payload
    },
    setUserHomeCoordinates(state, payload) {
      state.userHomeCoordinates = payload
    },
    setSavedStationCoordinates(state, payload) {
      state.savedStationCoordinates = payload
    },
    setStationCoordinates(state, payload) {
      state.stationCoordinates = payload
    },
    setActiveStationId(state, payload) {
      state.activeStationId = payload
    },
    setActiveStation(state, payload) {
      state.activeStation = payload
    },
    setHoveredStation(state, payload) {
      state.hoveredStation = payload
    },
    setFavoriteStation(state, payload) {
      state.favoriteStation = payload
    },
    setIsLoadingStationBookingInfo(state, payload) {
      state.isLoadingStationBookingInfo = payload
    },
    addStationBookingInfo(state, payload) {
      state.stationBookingInfo.push(payload)
    },
    clearStationBookingInfo(state) {
      state.stationBookingInfo = []
    },
    addTelephoneStationIds(state, payload) {
      state.telephoneStationIds.push(payload)
    },
    clearTelephoneStationIds(state) {
      state.telephoneStationIds = []
    },
    setTelephoneStationText(state, payload) {
      state.telephoneStationText = payload
    },
    addKeineTermineStationIds(state, payload) {
      state.keineTermineStationIds.push(payload)
    },
    clearKeineTermineStationIds(state) {
      state.keineTermineStationIds = []
    },
    addUnbookableStationIds(state, payload) {
      state.unbookableStationIds.push(payload)
    },
    clearUnbookableStationIds(state) {
      state.unbookableStationIds = []
    },
		setAllStations(state, payload) {
			state.allStations = payload;
		}
  },
  getters: {
    mapboxAccessToken(state) {
      return state.mapboxAccessToken
    },
    mapboxJsAccessToken(state) {
      return state.mapboxJsAccessToken
    },
    showMapboxCookieDialog(state) {
      return state.showMapboxCookieDialog
    },
    initMap(state) {
      return state.initMap
    },
    mainPostcode(state) {
      return state.mainPostcode
    },
    userLocationCoordinates(state) {
      return state.userLocationCoordinates
    },
    userHomeCoordinates(state) {
      return state.userHomeCoordinates
    },
    savedStationCoordinates(state) {
      return state.savedStationCoordinates
    },
    stationCoordinates(state) {
      return state.stationCoordinates
    },
    activeStationId(state) {
      return state.activeStationId
    },
    activeStation(state) {
      return state.activeStation
    },
    hoveredStation(state) {
      return state.hoveredStation
    },
    favoriteStation(state) {
      return state.favoriteStation
    },
    isLoadingStationBookingInfo(state) {
      return state.isLoadingStationBookingInfo
    },
    stationBookingInfo(state) {
      return state.stationBookingInfo
    },
    buchungInternetText(state) {
      // remove null
      const texts = state.stationBookingInfo.reduce((prev, curr) => {
        if (curr.buchungInternetText) prev.push(curr.buchungInternetText)
        return prev
      }, [])

      // remove duplicate text
      return texts.length
        ? texts
            .reduce(
              (previousValue, currentValue) => {
                return !previousValue.includes(currentValue)
                  ? previousValue.concat(currentValue)
                  : previousValue
              },
              [texts[0]]
            )
            .join(' \n ')
        : ''
    },
    telephoneStationIds(state) {
      return state.telephoneStationIds
    },
    telephoneStationText(state) {
      return state.telephoneStationText
    },
    keineTermineStationIds(state) {
      return state.keineTermineStationIds
    },
    keineTermineText(state) {
      const texts = state.stationBookingInfo
        .map((obj) => obj.schalterKeineTermineText)
        .filter((el) => el)

      return texts.length ? texts.join(' \n ') : ''
    },
    unbookableStationIds(state) {
      return state.unbookableStationIds
    },
		allStations(state) {
			return state.allStations;
		}
  },
}
